/**
 * Groups page — Swedish editorial "ledger" aesthetic.
 *
 * Reads like flipping through a bound index in a Stockholm law firm:
 * Fraunces serif for proper nouns, JetBrains Mono for counts and roles,
 * hairline rules instead of card chrome, a single lime swatch for the
 * default group, and a quiet left-margin spine running the length of
 * the page.
 *
 * All design tokens are scoped under `.ledger` in index.css — the rest
 * of the app keeps its neutral system-ui look. To extend this aesthetic
 * elsewhere, wrap the page in `<div className="ledger">`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  tenantApi,
  type GroupMember,
  type GroupRow,
  type UserRow,
} from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { useActiveGroup } from '../active-group-context.js';
import { usePageTitle } from '../use-page-title.js';

export function GroupsPage() {
  usePageTitle('Grupper');
  const { token, actor } = useTenantSession();
  const invalidate = useInvalidate();
  // Refreshing the active-group context after a create — its provider
  // hydrates from `groups.list` separately from SWR.
  const { refresh: refreshActiveGroupGroups } = useActiveGroup();

  const groupsQ = useTenantQuery(
    'groups.list',
    () => tenantApi.groups.list(),
    [],
    { ignoreActiveGroup: true },
  );
  const usersQ = useTenantQuery(
    'users.list',
    () => tenantApi.users.list(),
    [],
    { ignoreActiveGroup: true },
  );

  const groups: GroupRow[] | null = groupsQ.data?.groups ?? null;
  const users: UserRow[] | null = usersQ.data?.users ?? null;

  const [expanded, setExpanded] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUserIds, setNewUserIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Deep-link from the users page: `?group=<id>&user=<id>` auto-expands
  // the group, scrolls its row into view, and pulse-highlights the
  // matching member row once members finish loading. The params are
  // stripped after one application so a refresh or back-nav doesn't keep
  // re-triggering the effect. `scrollGroupId` and `highlightUserId` are
  // each one-shot — cleared after the receiving effect consumes them.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkGroupId = searchParams.get('group');
  const linkUserId = searchParams.get('user');
  const [scrollGroupId, setScrollGroupId] = useState<string | null>(null);
  const [highlightUserId, setHighlightUserId] = useState<string | null>(null);
  useEffect(() => {
    if (!linkGroupId || !groups) return;
    if (!groups.some((g) => g.id === linkGroupId)) {
      setSearchParams({}, { replace: true });
      return;
    }
    setExpanded(linkGroupId);
    setScrollGroupId(linkGroupId);
    if (linkUserId) setHighlightUserId(linkUserId);
    setSearchParams({}, { replace: true });
  }, [linkGroupId, linkUserId, groups, setSearchParams]);

  const canManage = actor?.tenantRole === 'org_admin';

  const toggleExpanded = (groupId: string) => {
    setExpanded((cur) => (cur === groupId ? null : groupId));
  };

  const create = async () => {
    if (!token || !newName.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.groups.create({
        name: newName.trim(),
        initial_user_ids: newUserIds,
      });
      setNewName('');
      setNewUserIds([]);
      setCreating(false);
      await invalidate('groups.list');
      await invalidate('groups.members');
      await refreshActiveGroupGroups();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addMember = async (groupId: string, userId: string) => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.groups.add_member({
        group_id: groupId,
        user_id: userId,
      });
      await Promise.all([
        invalidate('groups.members', groupId),
        invalidate('groups.list'),
      ]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (groupId: string, userId: string) => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.groups.remove_member({
        group_id: groupId,
        user_id: userId,
      });
      await Promise.all([
        invalidate('groups.members', groupId),
        invalidate('groups.list'),
      ]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setMemberRole = async (
    groupId: string,
    userId: string,
    role: 'admin' | 'normal',
  ) => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.groups.set_member_role({
        group_id: groupId,
        user_id: userId,
        role,
      });
      await invalidate('groups.members', groupId);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const disableGroup = async (groupId: string) => {
    if (!token) return;
    if (
      !window.confirm(
        'Inaktivera gruppen? Existerande medlemskap och historik bevaras, men gruppen blir inert: inga nya tilldelningar, inga skrivningar och dess innehåll visas inte längre.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.groups.disable({ group_id: groupId });
      if (expanded === groupId) setExpanded(null);
      await invalidate('groups.list');
      await refreshActiveGroupGroups();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const ssoJoinPolicy = groupsQ.data?.sso_join_policy ?? 'pending';
  const defaultGroupId = groupsQ.data?.default_group_id ?? null;

  const setSsoJoinPolicy = async (policy: 'pending' | 'default_group') => {
    if (!token || policy === ssoJoinPolicy) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.settings.set_sso_join_policy({ policy });
      await invalidate('groups.list');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const queryErr = groupsQ.error ?? usersQ.error;
  const displayErr = err ?? (queryErr ? (queryErr as Error).message : null);

  const totalMembers = useMemo(
    () => (groups ?? []).reduce((acc, g) => acc + g.member_count, 0),
    [groups],
  );

  return (
    <div className="ledger ledger-spine -mx-6 -my-6 min-h-[calc(100vh-3.5rem)] px-6 py-10">
      <Masthead
        count={groups?.length ?? null}
        totalMembers={totalMembers}
        canManage={canManage}
        creating={creating}
        onCreate={() => setCreating(true)}
      />

      {displayErr && <ErrorStrip message={displayErr} onDismiss={() => setErr(null)} />}

      {canManage && (
        <SsoJoinPolicyStrip
          policy={ssoJoinPolicy}
          hasDefaultGroup={defaultGroupId !== null}
          busy={busy}
          onChange={setSsoJoinPolicy}
        />
      )}

      {creating && canManage && (
        <CreateGroupForm
          name={newName}
          onNameChange={setNewName}
          users={users ?? []}
          selectedUserIds={newUserIds}
          onSelectedUserIdsChange={setNewUserIds}
          busy={busy}
          onCancel={() => {
            setCreating(false);
            setNewName('');
            setNewUserIds([]);
          }}
          onCreate={create}
        />
      )}

      {groups === null ? (
        <div className="ledger-meta mt-12 pl-14">Laddar register…</div>
      ) : groups.length === 0 ? (
        <EmptyState />
      ) : (
        <ol className="mt-8">
          {groups.map((g, i) => (
            <GroupEntry
              key={g.id}
              ordinal={i + 1}
              group={g}
              expanded={expanded === g.id}
              onToggle={() => toggleExpanded(g.id)}
              allUsers={users ?? []}
              canManage={canManage || g.my_role === 'admin'}
              canAdminTenant={canManage}
              busy={busy}
              onAdd={(userId) => addMember(g.id, userId)}
              onRemove={(userId) => removeMember(g.id, userId)}
              onSetRole={(userId, role) => setMemberRole(g.id, userId, role)}
              onDisable={() => disableGroup(g.id)}
              scrollIntoView={scrollGroupId === g.id}
              onScrollConsumed={() => setScrollGroupId(null)}
              highlightUserId={expanded === g.id ? highlightUserId : null}
              onHighlightConsumed={() => setHighlightUserId(null)}
            />
          ))}
        </ol>
      )}

      <Colophon />
    </div>
  );
}

/* ───────────────────────── Masthead ───────────────────────── */

function Masthead(props: {
  count: number | null;
  totalMembers: number;
  canManage: boolean;
  creating: boolean;
  onCreate: () => void;
}) {
  return (
    <header className="relative pl-14">
      <div className="ledger-meta absolute left-14 top-0 flex items-center gap-2">
        <span aria-hidden>※</span>
        <span>Register · Tenant Access</span>
      </div>

      <h1
        className="ledger-display mt-7 text-[clamp(2.5rem,5vw,4rem)] leading-[0.95]"
        style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 50, 'wght' 380" }}
      >
        Grupper
        <span
          className="ml-3 align-top text-[0.4em]"
          style={{ fontVariationSettings: "'opsz' 9, 'wght' 400" }}
        >
          {props.count !== null ? String(props.count).padStart(2, '0') : '—'}
        </span>
      </h1>

      <div className="mt-2 flex items-baseline gap-6">
        <p
          className="max-w-md text-[14px] leading-relaxed"
          style={{ color: 'var(--ledger-ink-soft)' }}
        >
          Din organisation är uppdelad i grupper — tänk på dem som mappar eller avdelningar. Varje avtal, fastighet och motpart tillhör alltid exakt en grupp. <br></br><br></br> När du skapar ett konto första gången hamnar du automatiskt i en standardgrupp. Efter det måste varje ny användare som bjuds in aktivt väljas in i rätt grupp — det sker inte automatiskt.
        </p>
        <div className="ml-auto flex items-center gap-6">
          <Tally label="Medlemskap" value={props.totalMembers} />
          {props.canManage && !props.creating && (
            <button
              type="button"
              onClick={props.onCreate}
              className="ledger-btn-primary"
            >
              + Ny grupp
            </button>
          )}
        </div>
      </div>

      <div className="ledger-rule mt-6" />
    </header>
  );
}

/* ─────────────────── SSO / domain-join policy ─────────────────── */

/**
 * Org-admin control for the group-assignment policy applied to members who
 * join the organisation via a verified domain / SSO connection — they never
 * pass through an invitation (ADR-0088). `pending` (secure default) parks
 * them with no access; `default_group` auto-seats them into the default group.
 */
function SsoJoinPolicyStrip(props: {
  policy: 'pending' | 'default_group';
  hasDefaultGroup: boolean;
  busy: boolean;
  onChange: (policy: 'pending' | 'default_group') => void;
}) {
  const failsSafe = props.policy === 'default_group' && !props.hasDefaultGroup;
  return (
    <section className="mt-8 pl-14">
      <div className="ledger-meta mb-2">Domän­anslutning · SSO</div>
      <div className="flex flex-wrap items-baseline gap-4">
        <p
          className="max-w-md text-[14px] leading-relaxed"
          style={{ color: 'var(--ledger-ink-soft)' }}
        >
          När en användare ansluter via er verifierade domän (SSO) utan
          inbjudan: låt dem vänta på manuell tilldelning, eller släpp in dem
          direkt i standardgruppen som vanlig medlem.
        </p>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={props.policy}
            onChange={(e) =>
              props.onChange(e.target.value as 'pending' | 'default_group')
            }
            disabled={props.busy}
            className="ledger-select"
            aria-label="Policy för domänanslutna medlemmar"
          >
            <option value="pending">Vänta på tilldelning</option>
            <option value="default_group">Släpp in i standardgrupp</option>
          </select>
        </div>
      </div>
      {failsSafe && (
        <p
          className="mt-2 text-[13px] italic"
          style={{ color: 'var(--ledger-ink-soft)' }}
        >
          Ingen standardgrupp är vald — domänanslutna användare hamnar i
          vänteläge tills en standardgrupp finns.
        </p>
      )}
      <div className="ledger-rule mt-6" />
    </section>
  );
}

function Tally({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-right">
      <div
        className="ledger-mono leading-none"
        style={{ fontSize: '28px', color: 'var(--ledger-ink)' }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div className="ledger-meta mt-1">{label}</div>
    </div>
  );
}

/* ───────────────────────── Group entry ───────────────────────── */

function GroupEntry(props: {
  ordinal: number;
  group: GroupRow;
  expanded: boolean;
  onToggle: () => void;
  allUsers: UserRow[];
  canManage: boolean;
  /** True only for tenant org-admin — controls group-level actions
   *  (archive), not member-roster edits which are also delegated to
   *  group-admins. */
  canAdminTenant: boolean;
  busy: boolean;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
  onSetRole: (userId: string, role: 'admin' | 'normal') => void;
  onDisable: () => void;
  /** Set by the deep-link effect on the row we navigated in to. The
   *  effect fires once after expansion, scrolls the row into view, then
   *  calls onScrollConsumed so the parent clears it. */
  scrollIntoView: boolean;
  onScrollConsumed: () => void;
  /** User id to scroll-and-pulse within the expanded member list. Null
   *  on every row except the deep-linked one. Cleared via
   *  onHighlightConsumed once the highlight effect runs. */
  highlightUserId: string | null;
  onHighlightConsumed: () => void;
}) {
  const { group: g } = props;
  const rowRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!props.scrollIntoView || !rowRef.current) return;
    rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    props.onScrollConsumed();
  }, [props.scrollIntoView]);
  // Members are fetched per-group via SWR keyed on the group id, so
  // addMember/removeMember can invalidate by exact group and only the
  // affected entry refetches. `enabled` ties the fetch to expanded
  // state — when the row collapses the key goes null, when it opens
  // again SWR picks up the cached entry (or revalidates).
  const membersQ = useTenantQuery(
    'groups.members',
    (gid) => tenantApi.groups.members({ group_id: gid as string }),
    [g.id],
    {
      ignoreActiveGroup: true,
      enabled: props.expanded,
      revalidateOnFocus: false,
    },
  );
  const members: GroupMember[] | undefined = membersQ.data?.members;

  return (
    <li className="ledger-row" ref={rowRef}>
      <button
        type="button"
        onClick={props.onToggle}
        className="ledger-entry group block w-full cursor-pointer text-left"
      >
        <div className="grid grid-cols-[56px_1fr_auto_auto] items-baseline gap-6 py-5 pl-2 pr-3">
          <span
            className="ledger-idx ledger-mono"
            style={{
              color: 'var(--ledger-ink-mute)',
              fontSize: '12px',
            }}
          >
            {String(props.ordinal).padStart(2, '0')}
          </span>

          <div className="flex items-baseline gap-3">
            {g.is_default && (
              <span
                className="ledger-swatch"
                aria-label="Default-grupp"
                title="Default-grupp — bootstrap-mål för tenantens första användare"
              />
            )}
            <span
              className="ledger-display"
              style={{
                fontSize: '22px',
                fontVariationSettings: "'opsz' 36, 'SOFT' 40, 'wght' 420",
              }}
            >
              {g.name}
            </span>
            {g.my_role === 'admin' && (
              <span
                className="ledger-meta"
                style={{ color: 'var(--ledger-accent)' }}
              >
                ★ Du är admin
              </span>
            )}
            {g.notifications_mailbox && (
              <span
                className="ledger-mono"
                style={{
                  color: 'var(--ledger-ink-mute)',
                  fontSize: '11px',
                }}
              >
                {g.notifications_mailbox}
              </span>
            )}
          </div>

          <span
            className="ledger-mono text-right"
            style={{
              fontSize: '13px',
              color: 'var(--ledger-ink-soft)',
              minWidth: '90px',
            }}
          >
            {String(g.member_count).padStart(2, '0')}{' '}
            <span style={{ color: 'var(--ledger-ink-mute)' }}>
              {g.member_count === 1 ? 'medlem' : 'medlemmar'}
            </span>
          </span>

          <span
            className="ledger-meta tabular-nums"
            aria-hidden
            style={{
              color: 'var(--ledger-ink-mute)',
              minWidth: '24px',
              textAlign: 'right',
            }}
          >
            {props.expanded ? '— Stäng' : 'Visa →'}
          </span>
        </div>
        <div className="ledger-rule" />
      </button>

      {props.expanded && (
        <GroupDetail
          group={g}
          members={members}
          allUsers={props.allUsers}
          canManage={props.canManage}
          canAdminTenant={props.canAdminTenant}
          busy={props.busy}
          onAdd={props.onAdd}
          onRemove={props.onRemove}
          onSetRole={props.onSetRole}
          onDisable={props.onDisable}
          highlightUserId={props.highlightUserId}
          onHighlightConsumed={props.onHighlightConsumed}
        />
      )}
    </li>
  );
}

/* ───────────────────────── Detail panel ───────────────────────── */

function GroupDetail(props: {
  group: GroupRow;
  members: GroupMember[] | undefined;
  allUsers: UserRow[];
  canManage: boolean;
  canAdminTenant: boolean;
  busy: boolean;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
  onSetRole: (userId: string, role: 'admin' | 'normal') => void;
  onDisable: () => void;
  highlightUserId: string | null;
  onHighlightConsumed: () => void;
}) {
  // Apply the pulse-highlight + scroll once the deep-linked member's row
  // actually exists in the DOM. The roster is fetched lazily so members
  // may be undefined on first render — wait for them.
  const highlightRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!props.highlightUserId || !props.members) return;
    const found = props.members.some((m) => m.userId === props.highlightUserId);
    if (!found) {
      // User has been removed from this group since the link was created.
      props.onHighlightConsumed();
      return;
    }
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Clear after the CSS animation finishes (2s) so the user can re-click
    // the same link and see the pulse again on the next navigation.
    const t = window.setTimeout(() => props.onHighlightConsumed(), 2200);
    return () => window.clearTimeout(t);
  }, [props.highlightUserId, props.members]);

  const memberIds = useMemo(
    () => new Set((props.members ?? []).map((m) => m.userId)),
    [props.members],
  );
  const addableUsers = props.allUsers.filter((u) => !memberIds.has(u.id));
  const [addUserId, setAddUserId] = useState('');

  return (
    <div className="ledger-unfold pl-14 pr-6 pb-8 pt-5">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-[1fr_280px] md:gap-12">
        {/* Left: member roster */}
        <section>
          <div className="ledger-meta mb-3">Medlemmar</div>
          {props.members === undefined ? (
            <div
              className="ledger-meta"
              style={{ color: 'var(--ledger-ink-mute)' }}
            >
              Laddar…
            </div>
          ) : props.members.length === 0 ? (
            <div
              className="ledger-meta italic"
              style={{ color: 'var(--ledger-ink-mute)', letterSpacing: 0, textTransform: 'none', fontSize: '13px' }}
            >
              Inga medlemmar förtecknade.
            </div>
          ) : (
            <ul>
              {props.members.map((m, i) => (
                <li
                  key={m.userId}
                  ref={m.userId === props.highlightUserId ? highlightRef : undefined}
                  className={m.userId === props.highlightUserId ? 'ledger-highlight' : undefined}
                >
                  <div className="grid grid-cols-[24px_1fr_auto_auto] items-baseline gap-4 py-2.5">
                    <span
                      className="ledger-mono"
                      style={{
                        fontSize: '10px',
                        color: 'var(--ledger-ink-mute)',
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0">
                      <div
                        style={{
                          fontFamily: 'var(--ledger-serif)',
                          fontSize: '15px',
                          fontVariationSettings: "'opsz' 24, 'wght' 400",
                        }}
                        className="truncate"
                      >
                        {m.name ?? m.email}
                      </div>
                      <div
                        className="ledger-mono truncate"
                        style={{
                          fontSize: '11px',
                          color: 'var(--ledger-ink-mute)',
                        }}
                      >
                        {m.email}
                      </div>
                    </div>
                    {props.canManage ? (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          props.onSetRole(
                            m.userId,
                            e.target.value as 'admin' | 'normal',
                          )
                        }
                        disabled={props.busy}
                        onClick={(e) => e.stopPropagation()}
                        className="ledger-select"
                      >
                        <option value="normal">Medlem</option>
                        <option value="admin">Gruppadmin</option>
                      </select>
                    ) : (
                      <span
                        className="ledger-meta"
                        style={{ color: 'var(--ledger-ink-soft)' }}
                      >
                        {m.role === 'admin' ? 'Gruppadmin' : 'Medlem'}
                      </span>
                    )}
                    {props.canManage && !props.group.is_default ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onRemove(m.userId);
                        }}
                        disabled={props.busy}
                        className="ledger-btn-link"
                      >
                        Avregistrera
                      </button>
                    ) : (
                      <span aria-hidden style={{ width: '1px' }} />
                    )}
                  </div>
                  <div className="ledger-rule" />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Right: aside panel */}
        <aside
          className="border-l pl-8"
          style={{ borderColor: 'var(--ledger-rule)' }}
        >
          {props.group.is_default && (
            <div className="mb-6">
              <div className="ledger-meta mb-2">Status</div>
              <p
                style={{
                  fontFamily: 'var(--ledger-serif)',
                  fontSize: '14px',
                  fontStyle: 'italic',
                  fontVariationSettings: "'opsz' 18, 'wght' 380",
                  color: 'var(--ledger-ink-soft)',
                  lineHeight: 1.55,
                }}
              >
                Default-gruppen är bootstrap-mål för tenantens första
                användare. Övriga inbjudningar måste välja grupp explicit.
              </p>
            </div>
          )}

          {props.canManage && addableUsers.length > 0 && (
            <div>
              <div className="ledger-meta mb-2">Lägg till medlem</div>
              <div className="flex items-center gap-2">
                <select
                  value={addUserId}
                  onChange={(e) => setAddUserId(e.target.value)}
                  className="ledger-select flex-1"
                  disabled={props.busy}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">— Välj användare —</option>
                  {addableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? u.email}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={!addUserId || props.busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (addUserId) {
                      props.onAdd(addUserId);
                      setAddUserId('');
                    }
                  }}
                  className="ledger-btn-primary"
                  style={{ padding: '0.55rem 0.9rem' }}
                >
                  Skriv in
                </button>
              </div>
            </div>
          )}

          {props.canManage && addableUsers.length === 0 && (
            <div>
              <div className="ledger-meta mb-1">Lägg till medlem</div>
              <p
                className="ledger-meta italic"
                style={{
                  textTransform: 'none',
                  letterSpacing: 0,
                  fontSize: '12px',
                  color: 'var(--ledger-ink-mute)',
                  fontFamily: 'var(--ledger-serif)',
                  fontStyle: 'italic',
                }}
              >
                Alla användare är redan medlemmar.
              </p>
            </div>
          )}

          {props.canAdminTenant && (
            <div className="mt-8">
              <div className="ledger-meta mb-2">Åtgärder</div>
              <div className="flex flex-col items-start gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onDisable();
                  }}
                  disabled={props.busy}
                  className="ledger-btn-link"
                >
                  Inaktivera grupp
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ───────────────────────── Create form ───────────────────────── */

function CreateGroupForm(props: {
  name: string;
  onNameChange: (v: string) => void;
  users: UserRow[];
  selectedUserIds: string[];
  onSelectedUserIdsChange: (ids: string[]) => void;
  busy: boolean;
  onCancel: () => void;
  onCreate: () => void;
}) {
  const toggle = (id: string) => {
    props.onSelectedUserIdsChange(
      props.selectedUserIds.includes(id)
        ? props.selectedUserIds.filter((x) => x !== id)
        : [...props.selectedUserIds, id],
    );
  };

  return (
    <section
      className="ledger-unfold mt-8 grid grid-cols-1 gap-8 md:grid-cols-[1fr_280px] md:gap-12 pl-4 pr-4 pb-10 pt-7 md:pl-14 md:pr-6"
      style={{
        background: 'var(--ledger-paper-soft)',
        borderTop: '1px solid var(--ledger-rule-strong)',
        borderBottom: '1px solid var(--ledger-rule-strong)',
      }}
    >
      <div>
        <div className="ledger-meta mb-1">Ny entry</div>
        <h2
          className="ledger-display"
          style={{
            fontSize: '32px',
            fontVariationSettings: "'opsz' 60, 'SOFT' 40, 'wght' 380",
          }}
        >
          <em
            className="not-italic"
            style={{ fontStyle: 'italic', fontFamily: 'var(--ledger-serif)' }}
          >
            Förteckna
          </em>{' '}
          en grupp
        </h2>

        <div className="mt-6 max-w-lg">
          <label className="ledger-meta mb-1 block">Beteckning</label>
          <input
            type="text"
            value={props.name}
            onChange={(e) => props.onNameChange(e.target.value)}
            placeholder="t.ex. Stockholm Söder, Förvaltning Norr…"
            className="ledger-input"
            style={{
              fontFamily: 'var(--ledger-serif)',
              fontSize: '20px',
              fontVariationSettings: "'opsz' 30, 'wght' 380",
            }}
            disabled={props.busy}
            autoFocus
          />
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={props.onCreate}
            disabled={props.busy || !props.name.trim()}
            className="ledger-btn-primary"
          >
            Skapa grupp
          </button>
          <button
            type="button"
            onClick={props.onCancel}
            disabled={props.busy}
            className="ledger-btn-ghost"
          >
            Avbryt
          </button>
          <span
            className="ledger-meta ml-3"
            style={{ color: 'var(--ledger-ink-mute)' }}
          >
            {props.selectedUserIds.length === 0
              ? 'Inga medlemmar valda'
              : `${String(props.selectedUserIds.length).padStart(2, '0')} valda`}
          </span>
        </div>
      </div>

      <aside
        className="border-l pl-8"
        style={{ borderColor: 'var(--ledger-rule)' }}
      >
        <div className="ledger-meta mb-3">Inledande medlemmar</div>
        {props.users.length === 0 ? (
          <div
            className="ledger-meta italic"
            style={{
              color: 'var(--ledger-ink-mute)',
              letterSpacing: 0,
              textTransform: 'none',
              fontSize: '12px',
              fontFamily: 'var(--ledger-serif)',
              fontStyle: 'italic',
            }}
          >
            Inga användare i registret ännu.
          </div>
        ) : (
          <ul
            className="max-h-72 overflow-y-auto"
            style={{ scrollbarColor: 'var(--ledger-rule-strong) transparent' }}
          >
            {props.users.map((u) => {
              const checked = props.selectedUserIds.includes(u.id);
              return (
                <li key={u.id}>
                  <label
                    className="flex cursor-pointer items-center gap-3 py-2.5"
                    style={{
                      borderBottom: '1px solid var(--ledger-rule)',
                    }}
                  >
                    <span
                      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center border"
                      style={{
                        borderColor: checked
                          ? 'var(--ledger-ink)'
                          : 'var(--ledger-rule-strong)',
                        background: checked
                          ? 'var(--ledger-ink)'
                          : 'transparent',
                      }}
                    >
                      {checked && (
                        <span
                          className="ledger-mono"
                          style={{
                            color: 'var(--ledger-lime)',
                            fontSize: '9px',
                            lineHeight: 1,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(u.id)}
                      className="sr-only"
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate"
                        style={{
                          fontFamily: 'var(--ledger-serif)',
                          fontSize: '14px',
                          fontVariationSettings: "'opsz' 20, 'wght' 400",
                        }}
                      >
                        {u.name ?? u.email}
                      </span>
                      <span
                        className="ledger-mono block truncate"
                        style={{
                          fontSize: '10px',
                          color: 'var(--ledger-ink-mute)',
                        }}
                      >
                        {u.email}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
    </section>
  );
}

/* ───────────────────────── Bits ───────────────────────── */

function ErrorStrip({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div
      className="mt-6 flex items-baseline gap-4 pl-14 pr-6 py-3"
      style={{
        background: 'var(--ledger-paper-soft)',
        borderLeft: '3px solid var(--ledger-ember)',
      }}
    >
      <span
        className="ledger-meta"
        style={{ color: 'var(--ledger-ember)' }}
      >
        Fel
      </span>
      <span
        style={{
          fontFamily: 'var(--ledger-serif)',
          fontStyle: 'italic',
          fontSize: '14px',
          color: 'var(--ledger-ink-soft)',
        }}
        className="flex-1"
      >
        {message}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="ledger-btn-link"
        style={{ color: 'var(--ledger-ink-mute)' }}
      >
        Dölj
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mt-16 pl-14 pr-6">
      <div
        className="border-l pl-6"
        style={{ borderColor: 'var(--ledger-rule-strong)' }}
      >
        <div className="ledger-meta mb-3">Registret är tomt</div>
        <p
          style={{
            fontFamily: 'var(--ledger-serif)',
            fontSize: '20px',
            fontStyle: 'italic',
            color: 'var(--ledger-ink-soft)',
            fontVariationSettings: "'opsz' 32, 'wght' 360",
            lineHeight: 1.4,
            maxWidth: '32rem',
          }}
        >
          Inga grupper förtecknade. En org-admin behöver lägga upp den första
          posten innan användare kan tilldelas riktig åtkomst.
        </p>
      </div>
    </div>
  );
}

function Colophon() {
  return (
    <div
      className="mt-20 pl-14 pr-6"
      style={{ color: 'var(--ledger-ink-mute)' }}
    >
      <div className="ledger-rule mb-4" />
      <div className="flex items-baseline justify-between">
        <span className="ledger-meta">Slut på register</span>
        <span
          className="ledger-mono"
          style={{ fontSize: '10px' }}
        >
          REGTA · TENANT-ACCESS · v1
        </span>
      </div>
    </div>
  );
}
